import { Flex } from "@portal/BaseComponents/Flex/Flex";
import {
  Form,
  FormProvider,
  useForm,
  useFormWatch,
} from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { InputNumber } from "@portal/BaseComponents/Input/AntInput";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { InvoiceSearchWrapper } from "@portal/BaseComponents/InvoicesSearch/InvoiceSearchWrapper";
import { onSelectedValuesChange } from "@portal/BaseComponents/InvoicesSearch/type";
import { Modal } from "@portal/BaseComponents/Modal/AntModal";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormFooter } from "@portal/BaseComponents/UI/FormArea/FormFooter";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { Icon } from "@portal/lib/icons/icons";
import {
  useCreateBulkPTPQuery,
  useCreateCustomerLevelPTPQuery,
  useCreatePTPQuery,
  useCustomerLevelPTPRoomQuery,
} from "@portal/queries/ptp-query";
import { Invoice } from "@portal/types/entities/invoice";
import {
  CreateCustomerLevelPTP,
  CreatePTP,
  PTPfieldIds,
} from "@portal/types/entities/ptp";
import { Unpacked } from "@portal/types/utils";
import { getCurrencySymbol } from "@portal/util/number-formatter";
import produce from "immer";
import { filter, subtract, sum } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PTPAmountAttribute,
  PTPDateForm,
  PTPNotesForm,
} from "../PTPFormComponents";
import useCustomerLevelAmountValidator from "../useCustomerLevelAmountValidator";
import { AgainstInvoicesCheckbox } from "../AgainstInvoicesCheckbox";

const PTPIcon = (
  <Icon
    icon={["far", "handshake-simple"]}
    color="var(--cyan-6)"
    size="2x"
    width="24px"
  />
);

interface PTPModalProps {
  open: boolean;
  onClose: () => void;
  getUpdatedValues?: (value?: CreatePTP) => void;
  value?: CreatePTP;
  onOpenChange?: (open: false) => void;
  defaultInvoice?: Invoice[];
  loading?: boolean;
}

type AmountErrors = {
  id: string;
  error: boolean;
};

type PTPFormValues = CreatePTP &
  Partial<CreateCustomerLevelPTP> & {
    [PTPfieldIds.against_invoices]?: boolean;
  };

type CustomerLevelAmountState = {
  validating: boolean;
  hasError: boolean;
};

const againstInvoicesSpaceConfig = {
  included: undefined,
  excluded: "sp-1",
} as const;
const amountDetailsPredicated = (
  invoice: Invoice
): Unpacked<CreatePTP["amount_details"]> => {
  return {
    invoice_id: invoice.id,
    amount: subtract(
      invoice.balance_due_amount,
      invoice.ptp_balance_amount ?? 0
    ),
  };
};

function PTPModal(props: PTPModalProps) {
  const {
    open,
    onClose,
    value,
    onOpenChange,
    getUpdatedValues,
    defaultInvoice,
  } = props;

  const [form] = useForm<PTPFormValues>();

  const amountDetails = useFormWatch(PTPfieldIds.amount_details, form);
  const againstInvoices =
    useFormWatch(PTPfieldIds.against_invoices, form) ?? true;
  const payDate = useFormWatch(PTPfieldIds.pay_date, form);
  const [customerLevelAmount, setCustomerLevelAmount] =
    useState<CustomerLevelAmountState>({ validating: false, hasError: false });
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice[] | undefined>(
    initializeSelectedInvoice
  );

  function getTotalAmount() {
    if (!amountDetails) return;
    return sum(amountDetails.map((f) => f.amount));
  }

  function initializeSelectedInvoice() {
    return defaultInvoice;
  }
  const [errors, setIsErrors] = useState<Array<AmountErrors>>();

  const createdSinglePTP = useCreatePTPQuery({
    onSuccessCallBack,
    getUpdatedValues,
  });
  const createBulkPtp = useCreateBulkPTPQuery({
    onSuccessCallBack,
    getUpdatedValues,
  });
  const createCustomerLevelPtp = useCreateCustomerLevelPTPQuery({
    onSuccessCallBack,
  });
  const customerLevelRoom = useCustomerLevelPTPRoomQuery({
    enabled: open && !againstInvoices,
    payDate,
  });
  const customerCurrency = customerLevelRoom.data?.currency;
  const customerLevelAmountRule = useCustomerLevelAmountValidator({
    enabled: !againstInvoices,
    payDate,
    currency: customerCurrency,
  });

  function handleClose() {
    onClose();
    setSelectedInvoice(undefined);
    setIsErrors(undefined);
    if (searchParams.has("entity_type")) {
      setSearchParams({}, { state: null, replace: true });
    }
  }

  function onSuccessCallBack() {
    setSelectedInvoice(undefined);
    handleClose();
  }
  async function createPTP(value: CreatePTP) {
    if (!selectedInvoice) return;
    if (selectedInvoice.length > 1) {
      createBulkPtp.mutateAsync(value);

      return;
    }

    createdSinglePTP.mutateAsync(value);
  }
  function createCustomerLevelPTP(values: PTPFormValues) {
    if (values.amount === undefined) return;
    createCustomerLevelPtp.mutateAsync({
      amount: values.amount,
      pay_date: values.pay_date,
      description: values.description,
    });
  }
  function handleSubmit() {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      if (againstInvoices) {
        createPTP({
          amount_details: values.amount_details,
          pay_date: values.pay_date,
          description: values.description,
        });
        return;
      }
      createCustomerLevelPTP(values);
    });
  }

  function handleCustomerLevelFieldsChange() {
    setCustomerLevelAmount({
      validating: form.isFieldValidating(PTPfieldIds.amount),
      hasError: form.getFieldError(PTPfieldIds.amount).length > 0,
    });
  }

  function handleErrorChange(value: string[]) {
    const updatedValues = filter(errors, (error: AmountErrors) => {
      return value.includes(error.id);
    });

    setIsErrors(updatedValues);
  }
  function handleInvoiceSearch(...args: onSelectedValuesChange) {
    const [value, options] = args;

    setSelectedInvoice(options);

    const amountDetails = options?.map(amountDetailsPredicated);
    form.setFieldValue(PTPfieldIds.amount_details, amountDetails);
    handleErrorChange(value);
  }

  function handleAmountChange(invoiceId: string, amount: number) {
    const details = form.getFieldValue(
      PTPfieldIds.amount_details
    ) as CreatePTP["amount_details"];

    const updateValues = produce(details, (draft) => {
      const index = details.findIndex((f) => f.invoice_id === invoiceId);
      draft[index].amount = amount;
      return draft;
    });

    form.setFieldValue(PTPfieldIds.amount_details, updateValues);
  }

  const totalAmount = useMemo(getTotalAmount, [amountDetails]);

  const HeaderTitle = (
    <Flex align="center" justify="start">
      {PTPIcon}
      <TypographyText fs="20">Set Promise to Pay</TypographyText>
    </Flex>
  );

  function getInvoiceIds() {
    return value?.amount_details.map((detail) => detail.invoice_id);
  }

  function handleOpenChange() {
    handleClose();
    onOpenChange?.(false);
  }

  const defaultInvoices = useMemo(getInvoiceIds, [value?.amount_details]);

  const Header = (
    <FormHeader title={HeaderTitle} onClose={handleClose} mode="modal" />
  );
  const Footer = (
    <FormFooter
      cancelButtonProps={{ onClick: handleClose }}
      saveButtonProps={{
        htmlType: "submit",
        onClick: handleSubmit,
        disabled: againstInvoices
          ? errors?.map((f) => f.error).includes(true)
          : customerLevelAmount.validating || customerLevelAmount.hasError,
        loading:
          createdSinglePTP.isLoading ||
          createBulkPtp.isLoading ||
          createCustomerLevelPtp.isLoading,
      }}
    />
  );
  function clearInvoiceLevelState() {
    setSelectedInvoice(undefined);
    setIsErrors(undefined);
    form.setFieldValue(PTPfieldIds.amount_details, undefined);
    form.setFieldValue(PTPfieldIds.amount, undefined);
  }

  useEffect(() => {
    if (!againstInvoices) clearInvoiceLevelState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [againstInvoices]);

  const AgainstInvoices = (
    <StyledFormItem
      name={PTPfieldIds.against_invoices}
      valuePropName="checked"
      initialValue
      preserve={false}
      space={againstInvoicesSpaceConfig[againstInvoices ? "included" : "excluded"]}
    >
      <AgainstInvoicesCheckbox hasInvoices={Boolean(selectedInvoice?.length)} />
    </StyledFormItem>
  );
  const AvailableRoom = customerLevelRoom.data ? (
    <Flex align="center" gap="4px">
      <TypographyText type="secondary">Available to promise:</TypographyText>
      <AmountText
        amount={customerLevelRoom.data.available_amount}
        currency={customerLevelRoom.data.currency}
        format="FULL"
      />
    </Flex>
  ) : null;
  const CustomerLevelAmount = (
    <StyledFormItem
      label="Payment Amount"
      space="sp-1"
      required
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.amount}
      validateFirst
      extra={AvailableRoom}
      rules={[
        { required: true, message: "This Field is Mandatory" },
        {
          type: "number",
          min: 0.01,
          message: "Amount must be greater than zero",
        },
        customerLevelAmountRule,
      ]}
    >
      <InputNumber
        prefix={getCurrencySymbol(customerCurrency)}
        controls={false}
        min={0}
        step="0.00"
        style={{ width: "100%" }}
      />
    </StyledFormItem>
  );
  const AssociatedInvoice = (
    <StyledFormItem
      space="sp-1"
      initialValue={
        defaultInvoices || defaultInvoice?.map((invoice) => invoice.id)
      }
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.amount_details}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <InvoiceSearchWrapper
        selectedValuesCallBack={handleInvoiceSearch}
        initialValue={defaultInvoice}
        openInvoicesOnly
      />
    </StyledFormItem>
  );
  const PTPDate = (
    <StyledFormItem
      label="PTP Date"
      space="sp-1"
      required
      initialValue={value?.pay_date}
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.pay_date}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <PTPDateForm />
    </StyledFormItem>
  );

  const AmountTable = (
    <StyledFormItem label="Payment Amount" required space="sp-1">
      <PTPAmountAttribute
        invoiceList={selectedInvoice}
        onAmountChange={handleAmountChange}
        totalAmount={totalAmount}
      />
    </StyledFormItem>
  );
  const PromissoryNote = (
    <StyledFormItem
      label="Promissory Note"
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.description}
    >
      <PTPNotesForm />
    </StyledFormItem>
  );

  const PTPForm = (
    <FormProvider
      onFormChange={(name, info) => {
        if (!name) return;
        const isError = info.forms[name].getFieldError("payment_amount");
        if (errors?.length) {
          const updatedValues = produce(errors, (draft) => {
            const index = draft.findIndex((f) => f.id === name);
            if (index === -1) {
              draft.push({ id: name, error: Boolean(isError.length) });
            } else {
              draft[index].error = Boolean(isError.length);
            }
          });
          setIsErrors(updatedValues);
        } else {
          setIsErrors([{ id: name, error: Boolean(isError.length) }]);
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        onFieldsChange={handleCustomerLevelFieldsChange}
        initialValues={{
          amount_details: defaultInvoice?.map(amountDetailsPredicated),
        }}
      >
        {PTPDate}
        {AgainstInvoices}
        {againstInvoices ? AssociatedInvoice : CustomerLevelAmount}
        {againstInvoices ? AmountTable : null}
        {PromissoryNote}
      </Form>
    </FormProvider>
  );

  return (
    <Modal
      open={open}
      forceRender={false}
      closable={false}
      title={Header}
      width={640}
      centered
      destroyOnClose
      footer={Footer}
      maskClosable
      onCancel={handleOpenChange}
      styles={{
        body: {
          overflow: "auto",
          maxHeight: "600px",
        },
      }}
    >
      {PTPForm}
    </Modal>
  );
}

export { PTPModal };
